/**
 * AdminInvoices.tsx — cross-tenant lista faktur (F7a T7.6).
 *
 * Model filtrowania „jak w katalogu" (poprawki-develop): CAŁE filtrowanie żyje w
 * nagłówkach kolumn (wspólny ConfigurableTable, client-side na pobranej liście).
 * Numer/Tenant — szukajka tekstowa; Status — dropdown wyboru; Wystawiona — zakres
 * dat od/do (pod kolumną, bez osobnego paska nad tabelą). Pobieramy do limitu (100).
 *
 * Data: `GET /api/admin/invoices?limit=100`.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Receipt } from 'lucide-react';

import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'overdue';

export interface InvoiceListItem {
  id: string;
  invoiceNumber: string | null;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  currency: string;
  status: InvoiceStatus;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  /** T8.7: ścieżka pliku PDF w MinIO; non-null = wygenerowano. */
  pdfPath: string | null;
  /** T8.7: timestamp ostatniej generacji PDF; null = nigdy. */
  generatedAt: string | null;
  gcrecord: string | null;
}

interface GeneratePdfResponse {
  pdfUrl: string;
  expiresAt: string;
  generatedAt: string;
}

interface GeneratePdfErrorBody {
  error: string;
  missing?: string[];
  detail?: string;
}

interface InvoicesResponse {
  invoices: InvoiceListItem[];
  total: number;
  limit: number;
  offset: number;
}

/** Górny limit pobrania — pod client-side filtry per kolumna. */
const LIST_FETCH_LIMIT = 100;

/** Kolejność statusów faktury — opcje filtra select w kolumnie „Status". */
const INVOICE_STATUSES: InvoiceStatus[] = ['draft', 'issued', 'paid', 'overdue'];

const INVOICE_STATUS_COLOR: Record<InvoiceStatus, BadgeColor> = {
  draft: 'gray',
  issued: 'blue',
  paid: 'green',
  overdue: 'red',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatMoney(groszy: number, currency: string): string {
  try {
    return (groszy / 100).toLocaleString('pl-PL', {
      style: 'currency',
      currency,
    });
  } catch {
    return `${(groszy / 100).toFixed(2)} ${currency}`;
  }
}

export function AdminInvoices() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [generatingId, setGeneratingId] = useState<string | null>(null);

  const generateMutation = useMutation<
    GeneratePdfResponse,
    { response?: { status: number; data: GeneratePdfErrorBody } } | Error,
    { invoiceId: string }
  >({
    mutationFn: async ({ invoiceId }) => {
      const res = await api.post<GeneratePdfResponse>(`/admin/invoices/${invoiceId}/generate`, {});
      return res.data;
    },
    onSuccess: (data) => {
      window.open(data.pdfUrl, '_blank');
      toast.show({
        variant: 'success',
        title: t('admin.invoices.generate.successTitle'),
        description: t('admin.invoices.generate.successDescription'),
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'invoices'] });
    },
    onError: (err) => {
      const body = (err as { response?: { data?: GeneratePdfErrorBody } }).response?.data;
      const code = body?.error ?? 'UNKNOWN';
      const missing = body?.missing?.join(', ');
      toast.show({
        variant: 'error',
        title: t('admin.invoices.generate.errorTitle', { code }),
        description: missing ?? body?.detail ?? t('admin.invoices.generate.errorGeneric'),
        duration: 8000,
      });
    },
    onSettled: () => {
      setGeneratingId(null);
    },
  });

  const handleGeneratePdf = (invoice: InvoiceListItem): void => {
    setGeneratingId(invoice.id);
    generateMutation.mutate({ invoiceId: invoice.id });
  };

  /**
   * T8.7: non-draft + pdf_path obecne → klik refreshuje URL, nie regeneruje pliku.
   * Wcześniejsza wersja (pre-Opcja 1) blokowała przycisk; po zmianie semantyki backendu
   * (route `/generate` zwraca 200 fresh URL bez renderu) przycisk jest zawsze aktywny.
   */
  const isRefreshOnly = (row: InvoiceListItem): boolean =>
    row.status !== 'draft' && row.pdfPath !== null;

  const invoicesQuery = useQuery<InvoicesResponse>({
    queryKey: ['admin', 'invoices'],
    queryFn: async () => {
      const res = await api.get<InvoicesResponse>(`/admin/invoices?limit=${LIST_FETCH_LIMIT}`);
      return res.data;
    },
  });

  const statusLabel = (status: InvoiceStatus): string =>
    t(`admin.invoices.status${status.charAt(0).toUpperCase() + status.slice(1)}`);

  const statusFilterOptions = useMemo(
    () => INVOICE_STATUSES.map((s) => ({ value: s, label: statusLabel(s) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  const columns = useMemo<ColumnDefinition<InvoiceListItem>[]>(
    () => [
      {
        id: 'invoiceNumber',
        accessor: (row) => row.invoiceNumber ?? '',
        header: t('admin.invoices.columns.number'),
        sortable: true,
        renderCell: (_value, row) =>
          row.invoiceNumber ? (
            <span className="font-mono text-xs">{row.invoiceNumber}</span>
          ) : (
            <span className="text-xs italic text-text-muted">{t('admin.invoices.noNumber')}</span>
          ),
      },
      {
        id: 'tenant',
        // Nazwa + slug złączone — szukajka tekstowa dziala po obu.
        accessor: (row) => `${row.tenantName} ${row.tenantSlug}`,
        header: t('admin.invoices.columns.tenant'),
        sortable: true,
        renderCell: (_value, row) => (
          <div className="flex flex-col">
            <span className="font-medium text-text-primary">{row.tenantName}</span>
            <span className="text-xs text-text-tertiary font-mono">{row.tenantSlug}</span>
          </div>
        ),
      },
      {
        id: 'status',
        accessor: (row) => row.status,
        header: t('admin.invoices.columns.status'),
        sortable: true,
        // Stany rozłączne → dropdown wyboru (client-side, exact-match).
        headerFilter: { category: 'select', options: statusFilterOptions },
        renderCell: (_value, row) => (
          <Badge color={INVOICE_STATUS_COLOR[row.status]}>{statusLabel(row.status)}</Badge>
        ),
      },
      {
        id: 'total',
        accessor: (row) => row.total,
        header: t('admin.invoices.columns.total'),
        sortable: true,
        // Kwota — bez szukajki (fraza nie ma sensu).
        filterable: false,
        align: 'right',
        renderCell: (_value, row) => (
          <span className="font-medium text-text-primary">
            {formatMoney(row.total, row.currency)}
          </span>
        ),
      },
      {
        id: 'issuedAt',
        // Data-only (YYYY-MM-DD) do porównania zakresu; renderCell pokazuje pełny format.
        accessor: (row) => (row.issuedAt ? row.issuedAt.slice(0, 10) : ''),
        header: t('admin.invoices.columns.issuedAt'),
        sortable: true,
        // Zakres dat od/do pod kolumną (zamiast osobnego paska nad tabelą).
        headerFilter: { category: 'date' },
        renderCell: (_value, row) => (
          <span className="text-xs text-text-secondary">{formatDate(row.issuedAt)}</span>
        ),
      },
      {
        id: 'dueAt',
        accessor: (row) => row.dueAt ?? '',
        header: t('admin.invoices.columns.dueAt'),
        sortable: true,
        // Data — bez szukajki (fraza nie ma sensu).
        filterable: false,
        renderCell: (_value, row) => (
          <span className="text-xs text-text-secondary">{formatDate(row.dueAt)}</span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, statusFilterOptions],
  );

  const renderActions = (row: InvoiceListItem) => {
    const refreshOnly = isRefreshOnly(row);
    const isGenerating = generatingId === row.id;
    const tooltip = refreshOnly
      ? t('admin.invoices.generate.refreshTooltip', {
          date: row.generatedAt ? new Date(row.generatedAt).toLocaleString('pl-PL') : '—',
        })
      : t('admin.invoices.generate.actionTooltip');
    const label = refreshOnly
      ? t('admin.invoices.generate.actionRefresh')
      : t('admin.invoices.generate.action');
    return (
      <div className="flex items-center justify-end">
        <Button
          variant="ghost"
          size="sm"
          disabled={isGenerating}
          title={tooltip}
          onClick={() => handleGeneratePdf(row)}
        >
          {isGenerating ? t('admin.invoices.generate.inProgress') : label}
        </Button>
      </div>
    );
  };

  const data = invoicesQuery.data;
  const rows = data?.invoices ?? [];
  const emptyMessage = t('admin.invoices.emptyNoData');

  return (
    <Layout title={t('admin.invoices.title')}>
      <section className="px-6 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-text-primary">{t('admin.invoices.title')}</h2>
          <p className="mt-1 text-sm text-text-tertiary">{t('admin.invoices.subtitle')}</p>
        </div>

        {invoicesQuery.isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={48} />
            ))}
          </div>
        ) : invoicesQuery.isError ? (
          <div
            role="alert"
            className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            <p>{t('admin.invoices.loadError')}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => void invoicesQuery.refetch()}
            >
              {t('admin.invoices.retry')}
            </Button>
          </div>
        ) : (
          <>
            <PreviewTenantAuthBoundary>
              <ConfigurableTable<InvoiceListItem>
                tableId="admin-invoices"
                columns={columns}
                data={rows}
                getRowId={(row) => row.id}
                renderActions={renderActions}
                actionsColumnLabel={t('admin.invoices.columns.actions')}
                persistConfig={false}
                showViewModeToggle={false}
                emptyIcon={Receipt}
                emptyTitle={emptyMessage}
                emptyDescription=""
              />
            </PreviewTenantAuthBoundary>
            {data && (
              <p className="mt-3 text-xs text-text-muted">
                {t('admin.invoices.countLabel', { count: data.total })}
                {data.total > rows.length &&
                  ' ' +
                    t('admin.invoices.countLabelPage', {
                      shown: rows.length,
                      total: data.total,
                    })}
              </p>
            )}
          </>
        )}
      </section>
    </Layout>
  );
}
